/**
 * Drawer / modal state as URL search params.
 *
 * A sheet's open/closed state is NEVER `useState` in the screen that renders
 * it. It is `?dialog=create-pool` or `?sheet=submit-picks&roundId=9`, which
 * buys three things component state cannot:
 *
 *  - browser back closes the top-most overlay before it leaves the screen;
 *  - a refresh mid-flow reopens exactly where the fan was;
 *  - `/pools?poolId=abc&dialog=submit-picks` is a real shareable link — a push
 *    notification target, or the URL in a support screenshot.
 *
 * These helpers are pure so they can be unit-tested without a router, and so a
 * route's `validateSearch` can reuse the same vocabulary the screens navigate
 * with.
 */

/** The two overlay slots. `dialog` layers over `sheet`, never the reverse. */
export const OVERLAY_KINDS = ["sheet", "dialog"] as const;
export type typeOverlayKind = (typeof OVERLAY_KINDS)[number];

export type typeOverlaySearch = {
  sheet?: string | undefined;
  dialog?: string | undefined;
};

export type typeOverlayDescriptor = {
  kind: typeOverlayKind;
  name: string;
};

/**
 * The overlay a fan would close first — the one browser-back should dismiss.
 * `dialog` wins because a confirm raised from inside a sheet sits on top of it.
 */
export function topOverlay(
  search: typeOverlaySearch,
): typeOverlayDescriptor | null {
  if (search.dialog !== undefined && search.dialog !== "") {
    return { kind: "dialog", name: search.dialog };
  }
  if (search.sheet !== undefined && search.sheet !== "") {
    return { kind: "sheet", name: search.sheet };
  }
  return null;
}

export function isOverlayOpen(
  search: typeOverlaySearch,
  kind: typeOverlayKind,
  name: string,
): boolean {
  return search[kind] === name;
}

/**
 * Returns the next search object with one overlay opened.
 *
 * `params` carries whatever that overlay is scoped to (`{ roundId: "9" }`).
 * Passing them here rather than setting them separately keeps the open action a
 * single history entry, so one back press closes the whole thing.
 */
export function openOverlay<TSearch extends typeOverlaySearch>(
  search: TSearch,
  overlay: typeOverlayDescriptor & { params?: Record<string, string> },
): TSearch {
  return {
    ...search,
    ...(overlay.params ?? {}),
    [overlay.kind]: overlay.name,
  };
}

/**
 * Returns the next search object with the top overlay closed, dropping the
 * params it owned.
 *
 * Clearing a key means REMOVING it, not setting it to `undefined` in place:
 * TanStack serializes an explicit `undefined` away, but leaving the key on the
 * object means every screen reading the search sees a property that exists with
 * no value, and `exactOptionalPropertyTypes` will not let that pass silently.
 */
export function closeOverlay<TSearch extends typeOverlaySearch>(
  search: TSearch,
  options?: { kind?: typeOverlayKind; clearParams?: readonly string[] },
): TSearch {
  const kind = options?.kind ?? topOverlay(search)?.kind;
  if (kind === undefined) return search;

  // Rebuilt by filtering rather than by deleting keys off a copy: the set of
  // keys to drop is computed, and a computed `delete` is both slower and the
  // kind of thing that quietly starts removing the wrong key when the shape
  // changes underneath it.
  const dropped = new Set<string>([kind, ...(options?.clearParams ?? [])]);
  return Object.fromEntries(
    Object.entries(search).filter(([key]) => !dropped.has(key)),
  ) as TSearch;
}
